import { ref } from 'vue';
import dayjs from "dayjs";
import i18n from "@/i18n/index";
import type { ManipulateType } from 'dayjs';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

// Extend jsPDF with autoTable
(jsPDF as any).API.autoTable = autoTable;
import { start } from '@popperjs/core';
import { Member } from '@/interfaces/member';
import { SubscriptionMember } from '@/interfaces/subscriptionMember';
import { useGlobal } from "@/composables/global/useGlobal";



export function usePDF() {
    const { formatDate, translateToFrench } = useGlobal();

    const ficheInscription = (member: Member, activeSubscription: SubscriptionMember) => {
        const doc = new jsPDF({
            orientation: "p",
            unit: "mm",
            format: "a4",
            putOnlyUsedFonts: true,
            precision: 20,
        });
        let startY = 0;
        startY = 15
        doc.setFont("helvetica", "normal", "bold");
        doc.setFontSize(11);
        let pageWidth = doc.internal.pageSize.width || doc.internal.pageSize.getWidth();
        let text = translateToFrench('views.clients.member_fiche.fiche_pdf.title');
        doc.text(text, pageWidth / 2, startY, { align: "center" });

        startY += 15
        doc.setFont("helvetica", "normal", "bold");
        doc.setFontSize(11);
        pageWidth = doc.internal.pageSize.width || doc.internal.pageSize.getWidth();
        text = translateToFrench('views.clients.member_fiche.fiche_pdf.member_info');
        doc.text(text, 15, startY, { align: "left" });

        const tableMember: string[] = [
            member?.matricule || "",
            member?.contractNumber || "",
            member?.name || "",
            member?.email || "",
            member?.cin || "",
            member?.tele || "",
            member?.adresse || "",
            member?.seller?.name || ""
        ];

        const columnsMember = [
            translateToFrench('tables.members.matricule'),
            translateToFrench('tables.members.contractNumber'),
            translateToFrench('tables.members.name'),
            translateToFrench('tables.members.email'),
            translateToFrench('tables.members.cin'),
            translateToFrench('tables.members.tele'),
            translateToFrench('tables.members.adresse'),
            translateToFrench('tables.members.seller')
        ];
        startY += 5;

        autoTable(doc, {
            margin: { top: 20 },
            startY: startY,
            pageBreak: "auto",
            theme: "striped",
            head: [columnsMember],
            body: [tableMember],
            foot: [],
            styles: {
                fontSize: 9, // Set the font size for the entire table
            },
            footStyles: {
                fontSize: 9, // Set the font size for the footer
            },
            headStyles: {
                fontSize: 9, // Set the font size for the header
                fillColor: [17, 69, 108],
                textColor: [255, 255, 255],
                cellPadding: 2,
            },
            bodyStyles: {
                fontSize: 9, // Set the font size for the body
                cellPadding: 2
            }
        });

        /**
         * Commercial (seller)
         */
        let finalY = (doc as any).lastAutoTable.finalY;
        if (member?.seller) {
            startY = finalY + 15
            doc.setFont("helvetica", "normal", "bold");
            doc.setFontSize(11);
            pageWidth = doc.internal.pageSize.width || doc.internal.pageSize.getWidth();
            text = translateToFrench('views.clients.member_fiche.fiche_pdf.seller_info');
            doc.text(text, 15, startY, { align: "left" });

            const tableSeller: string[] = [
                member?.seller?.matricule || "",
                member?.seller?.name || "",
                member?.seller?.email || "",
                member?.seller?.cin || "",
                member?.seller?.tele || "",
                member?.seller?.adresse || ""
            ];

            const columnsSeller = [
                translateToFrench('tables.trainer.matricule'),
                translateToFrench('tables.trainer.name'),
                translateToFrench('tables.trainer.email'),
                translateToFrench('tables.trainer.cin'),
                translateToFrench('tables.trainer.tele'),
                translateToFrench('tables.trainer.adresse')
            ];
            startY += 5;

            autoTable(doc, {
                margin: { top: 20 },
                startY: startY,
                pageBreak: "auto",
                theme: "striped",
                head: [columnsSeller],
                body: [tableSeller],
                foot: [],
                styles: {
                    fontSize: 9, // Set the font size for the entire table
                },
                footStyles: {
                    fontSize: 9, // Set the font size for the footer
                },
                headStyles: {
                    fontSize: 9, // Set the font size for the header
                    fillColor: [17, 69, 108],
                    textColor: [255, 255, 255],
                    cellPadding: 2,
                },
                bodyStyles: {
                    fontSize: 9, // Set the font size for the body
                    cellPadding: 2
                }
            });
        }


        /**
         * Active Subscription
         */
        // Get the final Y position after the first table
        finalY = (doc as any).lastAutoTable.finalY;
        if (activeSubscription) {
            startY = finalY + 15
            doc.setFont("helvetica", "normal", "bold");
            doc.setFontSize(11);
            pageWidth = doc.internal.pageSize.width || doc.internal.pageSize.getWidth();
            text = translateToFrench('views.clients.member_fiche.fiche_pdf.active_subscription');
            doc.text(text, 15, startY, { align: "left" });
            const tableActiveSubscription: string[] = [

                activeSubscription?.subscription?.name + ', ' + activeSubscription?.pack?.name,
                formatDate(activeSubscription?.start_date),
                formatDate(activeSubscription?.end_date),
                activeSubscription?.status?.remaining_days + ' ' + translateToFrench('symbole.day'),
                activeSubscription?.total_amount + ' ' + translateToFrench('symbole.currency.mad'),
                activeSubscription?.total_amount_paid + ' ' + translateToFrench('symbole.currency.mad'),
                activeSubscription?.remaining_amount + ' ' + translateToFrench('symbole.currency.mad'),
            ];

            const columnsActiveSubscription = [
                translateToFrench('tables.subscription_member.title'),
                translateToFrench('tables.subscription_member.start_date'),
                translateToFrench('tables.subscription_member.end_date'),
                translateToFrench('tables.subscription_member.remaining_days'),
                translateToFrench('tables.subscription_member.total_amount'),
                translateToFrench('tables.subscription_member.total_amount_paid'),
                translateToFrench('tables.subscription_member.remaining_amount')
            ];
            startY += 5;
            autoTable(doc, {
                margin: { top: 20 },
                startY: startY,
                pageBreak: "auto",
                theme: "striped",

                head: [columnsActiveSubscription],
                body: [tableActiveSubscription],
                foot: [],

                styles: {
                    fontSize: 9, // Set the font size for the entire table
                },
                footStyles: {
                    fontSize: 9, // Set the font size for the footer
                },
                headStyles: {
                    fontSize: 9, // Set the font size for the header
                    fillColor: [17, 69, 108],
                    textColor: [255, 255, 255],
                    cellPadding: 2,
                },
                bodyStyles: {
                    fontSize: 9, // Set the font size for the body
                    cellPadding: 2
                }
            });

        }

        doc.save("Fiche d'inscription" + member?.name);

    };


    return {
        ficheInscription
    };
}
